// Base
import { useState, useEffect, useRef } from "react";

// Store
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartStore } from "@/zustand/cartStore";

// Utils
import { useOrderCalculations } from "@/func/orderCalculations";

// Assets
import { ChevronIcon } from "@/assets/Icons";

// Components
import ExtendedWarrantyCheckbox from "@/components/OrderSummary/ExtendedWarrantyCheckbox";
import Box from "@/components/Box";

// Types
import type {
  OrderCalculations,
  ProductDataExtendedWarranty,
  PricingPackage,
} from "@/types/types";

interface OrderSummaryProps {
  className?: string;
}

export default function OrderSummary({ className }: OrderSummaryProps) {
  const productData = useProductData() as ProductDataExtendedWarranty;
  const { toggleOrderSummary, orderSummaryOpen, orderSummary, settings } =
    useTemplateStore();
  const [warrantyEnabled, setWarrantyEnabled] = useState<boolean>(false);
  const {
    selectedProduct,
    warrantyState,
    isDiscountApplied,
    setCartTotal,
    setWarrantyState,
  } = useCartStore();

  const multiplyWarranty = (productData as any)?.multiply_price_by_amount;
  const addWarrantyForProduct = (productData as any)?.add_warranty_for_product;

  const pricingPackage: PricingPackage | null = selectedProduct
    ? {
        price: selectedProduct.price,
        old_price: selectedProduct.old_price ?? selectedProduct.price,
        amount: selectedProduct.amount ?? 1,
        warranty_price: selectedProduct.warranty_price ?? 0,
        shipping_price: selectedProduct.shipping_price ?? 0,
      }
    : null;

  const orderCalculations: OrderCalculations = useOrderCalculations(
    pricingPackage,
    isDiscountApplied,
    multiplyWarranty,
    warrantyState ?? false,
    productData.formatted_string ?? undefined,
  ) ?? {
    subtotal: "0.00",
    shipping: "0.00",
    savings: "0.00",
    total: "0.00",
    total_numeric: "0.00",
  };

  useEffect(() => {
    setWarrantyEnabled(!!addWarrantyForProduct);
  }, [addWarrantyForProduct]);

  useEffect(() => {
    setWarrantyState(!!productData?.preselect_warranty);
  }, [productData?.preselect_warranty, setWarrantyState]);

  useEffect(() => {
    setCartTotal(orderCalculations.total_numeric);
  }, [orderCalculations.total_numeric, setCartTotal]);

  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(orderSummaryOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [orderSummaryOpen, orderCalculations, warrantyEnabled]);

  return (
    <Box
      className={`${className} shadow-cc mb-6 rounded-none p-4 md:mb-0 md:rounded-[10px]`}
    >
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={toggleOrderSummary}
      >
        <div
          className={`font-bold ${
            settings.enable_high_contrast ? "text-cc-dark-gray" : "text-cc-gray"
          }`}
        >
          {orderSummary?.tr_title}
        </div>

        <div
          className={`transition-transform duration-300 ${
            orderSummaryOpen ? "rotate-180" : ""
          }`}
        >
          <ChevronIcon />
        </div>
      </div>
      <div
        style={{
          maxHeight: `${height}px`,
          transition: "max-height 0.4s ease, opacity 0.3s ease",
          opacity: orderSummaryOpen ? 1 : 0,
        }}
        className="overflow-hidden"
      >
        <div ref={contentRef} className="pt-3">
          <div className="flex justify-between">
            <span
              className={`mb-2 font-bold ${
                settings.enable_high_contrast
                  ? "text-cc-dark-gray"
                  : "text-cc-gray"
              }`}
            >
              {selectedProduct?.amount ?? 1} x {selectedProduct?.display_name}{" "}
              {warrantyState && ` + ${orderSummary?.tr_warranty_label}`}
            </span>
            <span>{orderCalculations.subtotal}</span>
          </div>
          <div className="flex justify-between">
            <span
              className={`${
                settings.enable_high_contrast
                  ? "text-cc-dark-gray"
                  : "text-cc-gray"
              }`}
            >
              {orderSummary?.tr_shipping_label}
            </span>
            <span
              className={`${
                settings.enable_high_contrast
                  ? "text-cc-dark-gray"
                  : "text-cc-gray"
              }`}
            >
              {orderCalculations.shipping}
            </span>
          </div>
          {warrantyEnabled && (
            <>
              <div
                className={`mt-2 mb-2 h-px w-full ${
                  settings.enable_high_contrast ? "bg-cc-gray-5" : "bg-gray-300"
                }`}
              />
              <ExtendedWarrantyCheckbox />
            </>
          )}

          <div
            className={`mt-2 mb-2 h-px w-full ${
              settings.enable_high_contrast ? "bg-cc-gray-5" : "bg-gray-300"
            }`}
          />
          <div className="text-cc-green mb-4 flex justify-between text-sm">
            <span>{orderSummary?.tr_total_savings_label}</span>
            <span>-{orderCalculations.savings}</span>
          </div>

          <div
            className={`flex justify-between text-lg font-bold ${
              settings.enable_high_contrast
                ? "text-cc-dark-gray"
                : "text-cc-gray"
            }`}
          >
            <span>{orderSummary?.tr_total_label}</span>
            <span>{orderCalculations.total}</span>
          </div>
        </div>
      </div>
    </Box>
  );
}
